/**
 * Frontend client for the self-service agent factory.
 *
 * Wraps:
 *   GET /api/agents/templates                              list templates
 *   GET /api/agents/templates/:id                          fetch one
 *
 *   GET    /api/me/agents                                  owned + shared + available
 *   POST   /api/me/agents                                  create from template
 *   GET    /api/me/agents/:agentId                         fetch detail (owner only)
 *   PATCH  /api/me/agents/:agentId                         update fields
 *   DELETE /api/me/agents/:agentId                         soft delete
 *
 *   AA-210 sharing surface:
 *   GET    /api/me/agents/:agentId/sharing                 grants + allMembersEnabled
 *   POST   /api/me/agents/:agentId/sharing                 batch grant { userEmails: [] }
 *   DELETE /api/me/agents/:agentId/sharing/:userEmail      revoke
 *   GET    /api/me/agents/:agentId/sharepoint-members      site members for the picker
 *   PATCH  /api/me/agents/:agentId/sharing/all-members     toggle "share with all"
 */

import { API_BASE, authFetch } from '../pages/admin/constants';

export interface AgentTemplate {
  id: string;
  name: string;
  description: string;
  icon?: string;
  category?: string;
  agentType: 'rag' | 'orchestrator' | 'mcp_tool' | 'pipeline';
  instructions: string;
  modelOverride?: string | null;
  suggestedToolsets?: string[];
  defaultBehavior?: {
    temperature?: number;
    maxTokens?: number;
    requireCitations?: boolean;
  };
  sampleQuestions?: string[];
  requiresKnowledgeSource?: boolean;
  version: string;
}

export type AgentVisibility = 'private' | 'shared' | 'published';

export type IndexingStatus =
  | 'unconfigured'  // No source folder selected (not a RAG agent or skipped)
  | 'pending'       // Trigger fired, waiting for jobId
  | 'indexing'      // Indexing job is running
  | 'ready'         // Index is populated and the agent can answer
  | 'failed';       // Indexing job errored out

export interface MyAgent {
  agentId: string;
  name: string;
  description?: string;
  agentType?: string;
  instructions?: string;
  ownerEmail?: string | null;
  visibility?: AgentVisibility;
  /**
   * AA-215: when visibility==='published', narrows access to users
   * carrying at least one of these Entra roles. Null/empty means
   * "all users with default agent access". Stored on the AIAgent
   * node as a JSON-stringified array; the backend parses it into
   * a string[] before returning here.
   */
  allowedRoles?: string[] | null;
  templateId?: string | null;
  templateVersion?: string | null;
  status?: string;
  searchIndexAlias?: string | null;
  sourceFolder?: string | null;
  /**
   * Multi-folder selection persisted as a JSON array string on the
   * agent record. Most consumers should prefer this when present;
   * the legacy sourceFolder above mirrors the first entry for
   * back-compat with older readers.
   */
  sourceFolders?: string[] | null;
  sourceSite?: string | null;
  sourceSiteWebUrl?: string | null;
  indexingStatus?: IndexingStatus;
  indexingJobId?: string | null;
  indexingError?: string | null;
  indexingStartedAt?: string;
  indexingCompletedAt?: string;
  endpoint?: string;
  streamingEndpoint?: string;
  disabled?: boolean;
  createdAt?: string;
  updatedAt?: string;
  /**
   * Sharing-grant summary computed server-side and only attached to
   * the `owned` rows of /api/me/agents (not sharedWithMe / availableToMe).
   * `shareCount` is the total count of `allowed` grants; `shareEmails`
   * is a preview list — capped to 3 by the backend so the card can
   * render "alice@…, bob@… +2 more" without N round-trips.
   */
  shareCount?: number;
  shareEmails?: string[];
  // Many other fields may come through; keep loose.
  [key: string]: unknown;
}

export interface IndexingStatusResponse {
  agentId: string;
  status: IndexingStatus;
  jobId: string | null;
  error: string | null;
  progress?: unknown;
}

export interface SharingEntry {
  agentId: string;
  userEmail: string;
  accessLevel: 'allowed' | 'denied';
  addedBy: string;
  addedAt: string;
  notes?: string;
}

async function _ok<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body: unknown = undefined;
    try { body = await res.json(); } catch { /* not JSON */ }
    const message =
      (body && typeof body === 'object' && 'error' in body && typeof (body as { error: unknown }).error === 'string')
        ? (body as { error: string }).error
        : `${res.status} ${res.statusText}`;
    const err = new Error(message);
    (err as Error & { statusCode?: number }).statusCode = res.status;
    throw err;
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

export async function listTemplates(): Promise<AgentTemplate[]> {
  const res = await authFetch(`${API_BASE}/agents/templates`);
  const data = await _ok<{ templates: AgentTemplate[] }>(res);
  return data.templates;
}

export async function getTemplate(id: string): Promise<AgentTemplate> {
  const res = await authFetch(`${API_BASE}/agents/templates/${encodeURIComponent(id)}`);
  const data = await _ok<{ template: AgentTemplate }>(res);
  return data.template;
}

// ---------------------------------------------------------------------------
// My agents
// ---------------------------------------------------------------------------

/**
 * "Available to me" entries are published self-service agents the
 * user can access via an attribute gate (SharePoint membership,
 * Entra role, job code, or location code) but doesn't own and
 * isn't explicitly shared with. The `accessVia` / `accessDetail`
 * fields tell the UI WHY the user has access so the card can
 * label it correctly (e.g. "Via SharePoint: AskAldoKB").
 */
export type AccessVia = 'sharepoint_site' | 'role' | 'job_code' | 'location' | 'unknown';
export interface AvailableToMeAgent extends MyAgent {
  accessVia?: AccessVia;
  accessDetail?: string | null;
}

export async function listMyAgents(): Promise<{
  owned: MyAgent[];
  sharedWithMe: MyAgent[];
  availableToMe: AvailableToMeAgent[];
  total: number;
}> {
  const res = await authFetch(`${API_BASE}/me/agents`);
  // Defensive: older Express deploys don't return availableToMe.
  // Default to an empty array so the frontend doesn't crash on a
  // mid-rollout build skew.
  const data = await _ok<{
    owned: MyAgent[];
    sharedWithMe: MyAgent[];
    availableToMe?: AvailableToMeAgent[];
    total: number;
  }>(res);
  return {
    owned: data.owned,
    sharedWithMe: data.sharedWithMe,
    availableToMe: Array.isArray(data.availableToMe) ? data.availableToMe : [],
    total:
      data.total ??
      data.owned.length +
        data.sharedWithMe.length +
        (data.availableToMe?.length || 0),
  };
}

export interface CreateMyAgentInput {
  templateId: string;
  name: string;
  description?: string;
  agentId?: string;
  /** Legacy single-folder field. Stays for back-compat with older clients. */
  sourceFolder?: string;
  /**
   * Multi-folder selection (preferred). One or more folder paths
   * relative to the chosen SharePoint site's default document
   * library. The backend persists the array as JSON on the agent
   * record and the indexer fans out one ingestion call per folder
   * (sequentially, sharing a single search index).
   */
  sourceFolders?: string[];
  /** Graph site id (hostname:path or comma-separated id). */
  sourceSite?: string;
  sourceSiteWebUrl?: string;
  /**
   * Server forces this to 'private' under AA-210 — left in the
   * type for older clients that still send it.
   */
  visibility?: AgentVisibility;
  instructionsOverride?: string;
  /**
   * Routing metadata consumed by Aldo's planner. Without these the
   * agent is reachable only via direct selection from the dropdown.
   * All optional — backend falls back to the template's defaults.
   */
  sampleQuestions?: string[];
  routingTips?: string;
}

/**
 * Folder-overlap pre-create check (AA-210). The wizard calls this on
 * submit; if any existing self-service agent already indexes one of
 * the same (siteWebUrl, folder) pairs, we surface the conflict so the
 * owner can confirm before creating a duplicate. Soft check — the
 * server doesn't block creation, only the UI gate prevents it.
 */
export interface FolderOverlap {
  agentId: string;
  name: string;
  ownerEmail: string | null;
  visibility: string | null;
  indexingStatus: string | null;
  /** The folder paths that collide between the existing agent and the candidate. */
  overlappingFolders: string[];
  sourceSiteWebUrl: string | null;
}

export async function checkFolderOverlap(
  sourceSiteWebUrl: string,
  sourceFolders: string[]
): Promise<FolderOverlap[]> {
  if (!sourceSiteWebUrl || !sourceFolders?.length) return [];
  const res = await authFetch(`${API_BASE}/me/agents/check-folder-overlap`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sourceSiteWebUrl, sourceFolders }),
  });
  const data = await _ok<{ overlaps: FolderOverlap[] }>(res);
  return data.overlaps || [];
}

export async function createMyAgent(input: CreateMyAgentInput): Promise<MyAgent> {
  const res = await authFetch(`${API_BASE}/me/agents`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = await _ok<{ agent: MyAgent }>(res);
  return data.agent;
}

// ---------------------------------------------------------------------------
// Wizard helper: suggest sample questions from the connected SharePoint folder.
//
// Used by Build-an-Agent's Routing step so the user can seed the
// sample-questions list from the actual contents of their KB instead
// of writing them from scratch. The backend prompts an LLM with the
// document filenames in the chosen folder; the result is plain
// strings that drop straight into the existing editable inputs.
//
// `reason` is set when `questions` comes back empty so the UI can
// distinguish "we didn't find any indexable docs" from "the model
// returned nothing useful, try again".
// ---------------------------------------------------------------------------
export interface SuggestSampleQuestionsInput {
  siteId: string;
  /** Single-folder shape (legacy). Prefer folderPaths for new callers. */
  folderPath?: string;
  /**
   * Multi-folder shape. The backend unions filenames across these
   * folders before prompting, so suggestions reflect the agent's
   * full configured KB rather than just one slice of it. Empty
   * array (or omitted) falls back to folderPath, then to the
   * library root.
   */
  folderPaths?: string[];
  agentName?: string;
  agentDescription?: string;
  count?: number;
}

export interface SuggestSampleQuestionsResult {
  questions: string[];
  filenames: string[];
  reason?: 'no-indexable-documents' | 'completion-failed' | 'completion-empty';
}

export async function suggestSampleQuestionsFromKB(
  input: SuggestSampleQuestionsInput
): Promise<SuggestSampleQuestionsResult> {
  const res = await authFetch(`${API_BASE}/me/agents/suggest-sample-questions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = await _ok<{
    success: true;
    questions: string[];
    filenames: string[];
    reason?: SuggestSampleQuestionsResult['reason'];
  }>(res);
  return {
    questions: Array.isArray(data.questions) ? data.questions : [],
    filenames: Array.isArray(data.filenames) ? data.filenames : [],
    reason: data.reason,
  };
}

export async function getMyAgent(agentId: string): Promise<{ agent: MyAgent; sharing: SharingEntry[] }> {
  const res = await authFetch(`${API_BASE}/me/agents/${encodeURIComponent(agentId)}`);
  return _ok<{ agent: MyAgent; sharing: SharingEntry[] }>(res);
}

export interface UpdateMyAgentInput {
  name?: string;
  description?: string;
  instructions?: string;
  searchIndexAlias?: string;
  sourceFolder?: string;
  sourceSite?: string;
  sourceSiteWebUrl?: string;
  sampleQuestions?: string[];
  routingTips?: string;
}

export async function updateMyAgent(agentId: string, input: UpdateMyAgentInput): Promise<MyAgent> {
  const res = await authFetch(`${API_BASE}/me/agents/${encodeURIComponent(agentId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const data = await _ok<{ agent: MyAgent }>(res);
  return data.agent;
}

export async function deleteMyAgent(agentId: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/me/agents/${encodeURIComponent(agentId)}`, {
    method: 'DELETE',
  });
  await _ok(res);
}

/**
 * Poll the SharePoint folder ingestion job status for a self-service
 * agent. Express lazily writes terminal states (ready/failed) back onto
 * the AIAgent Neo4j node when the underlying job settles, so on a
 * subsequent listMyAgents() the badge will be accurate without polling
 * here at all — but for live UX we hit this endpoint every ~10s while
 * the agent is in pending/indexing.
 */
export async function getMyAgentIndexingStatus(
  agentId: string
): Promise<IndexingStatusResponse> {
  const res = await authFetch(
    `${API_BASE}/me/agents/${encodeURIComponent(agentId)}/indexing-status`
  );
  return _ok<IndexingStatusResponse>(res);
}

/**
 * Manually re-trigger the SharePoint folder ingestion for an agent.
 * Owner-only. Returns 202 immediately; the agent's indexingStatus flips
 * back to 'indexing' and the IndexingBadge polling loop will pick up
 * the terminal state when the Function App responds.
 */
export async function reindexMyAgent(
  agentId: string
): Promise<{ ok: boolean; agentId: string; status: IndexingStatus }> {
  const res = await authFetch(`${API_BASE}/me/agents/${encodeURIComponent(agentId)}/reindex`, {
    method: 'POST',
  });
  return _ok<{ ok: boolean; agentId: string; status: IndexingStatus }>(res);
}

// AA-210: setMyAgentVisibility, setMyAgentAllowedRoles, listKnownRoles,
// getMyAgentAudience, setMyAgentJobCodes, setMyAgentLocationCodes were
// removed. Visibility and audience are now derived from sharing
// actions on the Share tab — see addSharingGrants and
// setShareWithAllMembers below.

// ---------------------------------------------------------------------------
// Sharing (AA-210)
//
// The new self-service Share tab gets its members from the agent's
// connected SharePoint site instead of asking the owner to type
// emails by hand. The picker check-marks people, then a single
// batched POST grants them all at once. Visibility changes
// themselves are no longer exposed as a dropdown — they happen as
// a side effect of adding/removing grants and toggling the
// "Share with all members" switch.
// ---------------------------------------------------------------------------

export interface SharingState {
  agentId: string;
  sharing: SharingEntry[];
  /** True iff visibility==='published' — i.e. the all-SharePoint-members toggle is on. */
  allMembersEnabled: boolean;
}

export async function listSharing(agentId: string): Promise<SharingState> {
  const res = await authFetch(`${API_BASE}/me/agents/${encodeURIComponent(agentId)}/sharing`);
  return _ok<SharingState>(res);
}

/**
 * Grant the agent to one or more users at once. Single-email shape
 * still works against this client for legacy call sites; the new
 * Share tab uses the batched form.
 */
export async function addSharingGrants(
  agentId: string,
  userEmails: string[],
  notes?: string
): Promise<{
  ok: boolean;
  agentId: string;
  granted: string[];
  failed: Array<{ userEmail: string; error: string }>;
}> {
  const res = await authFetch(`${API_BASE}/me/agents/${encodeURIComponent(agentId)}/sharing`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userEmails, notes }),
  });
  return _ok(res);
}

/** Single-email convenience wrapper for legacy code paths. */
export async function addSharingGrant(
  agentId: string,
  userEmail: string,
  notes?: string
): Promise<void> {
  await addSharingGrants(agentId, [userEmail], notes);
}

export async function removeSharingGrant(agentId: string, userEmail: string): Promise<void> {
  const res = await authFetch(
    `${API_BASE}/me/agents/${encodeURIComponent(agentId)}/sharing/${encodeURIComponent(userEmail)}`,
    { method: 'DELETE' }
  );
  await _ok(res);
}

export interface SharePointSiteMember {
  email: string;
  displayName: string;
  jobTitle?: string | null;
}

export interface SharePointMembersResponse {
  hasSharePointSource: boolean;
  members: SharePointSiteMember[];
  totalCount: number;
  site: { id: string; displayName: string; webUrl: string } | null;
  error?: string;
  /**
   * AA-210: true iff the SharePoint app principal got 401/403 on
   * /sites/{id}/permissions. Surfaced so the Share tab can render
   * a more specific empty-state explanation (lacking
   * Sites.FullControl.All) instead of generic "no members".
   */
  permissionsAccessDenied?: boolean;
}

/**
 * Fetch up to `top` members of the agent's connected SharePoint
 * site, optionally filtered by a case-insensitive `search` substring
 * on email or displayName. The Share tab debounces typing and calls
 * this on each query change.
 *
 * If the agent has no SharePoint source (non-RAG template), the
 * server returns hasSharePointSource=false and the UI falls back
 * to plain email-typing.
 */
export async function listAgentSharePointMembers(
  agentId: string,
  opts: { search?: string; top?: number } = {}
): Promise<SharePointMembersResponse> {
  const params = new URLSearchParams();
  if (opts.search) params.set('search', opts.search);
  if (typeof opts.top === 'number') params.set('top', String(opts.top));
  const qs = params.toString();
  const url =
    `${API_BASE}/me/agents/${encodeURIComponent(agentId)}/sharepoint-members` +
    (qs ? `?${qs}` : '');
  const res = await authFetch(url);
  return _ok<SharePointMembersResponse>(res);
}

/**
 * Toggle "Share with all members of {SiteName}". Backed by visibility
 * = published; the SharePoint-membership gate handles enforcement.
 */
export async function setShareWithAllMembers(
  agentId: string,
  enabled: boolean
): Promise<{
  ok: boolean;
  agentId: string;
  allMembersEnabled: boolean;
  visibility: AgentVisibility;
  agent: MyAgent;
}> {
  const res = await authFetch(
    `${API_BASE}/me/agents/${encodeURIComponent(agentId)}/sharing/all-members`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    }
  );
  return _ok(res);
}
